import React, {useCallback} from "react";
import PropTypes from "prop-types";
import {AListItem, AMount} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {t} from "i18next";
import {useDispatch} from "react-redux";
import Chip from "components/Chip";
import {DropdownMenu} from "components/DropdownMenu";
import TooltipDelay from "components/TooltipDelay";
import {setIncidentStatus} from "store/features/incidentsSlice";
import {sendIncidentStatusTelemetry} from "api/telemetry/incidentsTelemetrics";
import {STATUS} from "models/options";
import css from "./StatusDropdownMenu.module.scss";

const StatusDropdownMenu = ({
  incident,
  truncate,
  useMount = false,
  chipProps,
  ...rest
}) => {
  const dispatch = useDispatch();
  const {status: currentStatus} = incident;

  const handleStatusChange = useCallback(
    (status) => {
      sendIncidentStatusTelemetry(status, useMount);
      dispatch(setIncidentStatus({incidents: [{...incident}], status}));
    },
    [incident]
  );
  const renderMenu = (
    <DropdownMenu
      className="status-dropdown"
      buttonClass={classNames("status-dropdown__btn", css.menuButton, "pa-0")}
      buttonVariant="tertiary"
      buttonContent={
        <Chip rightIcon="caret-down" truncateText={truncate} {...chipProps}>
          <TooltipDelay
            title={t("incidents.table.status")}
            subtitle={currentStatus}>
            <span>{currentStatus}</span>
          </TooltipDelay>
        </Chip>
      }
      closeMenuOnClick={true}
      chevron={false}
      buttonSpanProps={{
        className: css.menuButtonSpan
      }}
      data-testid={"StatusDropdownMenu-Dropdown"}
      menuClass="status-dropdown__menu"
      {...rest}>
      {STATUS.map((statusObj) => (
        <AListItem
          key={`status-option-${statusObj.value}`}
          className="status-dropdown__item"
          onClick={() => handleStatusChange(statusObj.value)}
          disabled={statusObj.value === currentStatus}>
          {statusObj.name}
        </AListItem>
      ))}
    </DropdownMenu>
  );

  return useMount ? (
    <AMount style={{position: "relative"}}>{renderMenu}</AMount>
  ) : (
    renderMenu
  );
};

export default StatusDropdownMenu;

StatusDropdownMenu.propTypes = {
  /**
   * Only defines subset of incident object required for this component.
   */
  incident: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string
  }).isRequired,
  /**
   * Optional Property. If true, component text will have a max-width of 121px.
   */
  truncate: PropTypes.bool,
  /**
   * Optionally pass props through to the inner Chip component.
   */
  chipProps: PropTypes.object,
  /**
   * If true, menu will be wrapped in AMount
   * @remark Fixes issues when menus scroll with page
   *
   * @defaultValue false
   */
  useMount: PropTypes.bool
};
